import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import {
  ButtonComponent,
  InputComponent,
  ResolvableModal,
  SOFTLINE_MODAL_RESOLVER,
  TranslatePipe,
} from '@softline/ui-core';
import {
  AddressFormValue,
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../../../types/document-form-data';
import { isCustomerAddressComplete } from '../../document-creation-wizard.functions';

const EMPTY_ADDRESS: AddressFormValue = {
  salutation: null,
  name1: null,
  name2: null,
  name3: null,
  street: null,
  countryCode: null,
  postCode: null,
  city: null,
  mail1: null,
};

/** Address of a walk-in customer, captured while creating the document: a
    Barverkauf account is a collector for many customers, so its master-data
    address says nothing about who is standing at the counter. Only reachable
    for such a customer (see `wizardStepKeys`) and mandatory there — the step
    turns the manual Kundenadresse on and cannot be left incomplete. */
@Component({
  selector: 'soft-sales-address-step',
  templateUrl: './address-step.component.html',
  styleUrl: './address-step.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-w-100' },
  imports: [ButtonComponent, InputComponent, TranslatePipe],
})
export class AddressStepComponent
  extends ResolvableModal<DocumentFormData>
  implements OnInit
{
  private readonly modalResolver = inject(SOFTLINE_MODAL_RESOLVER, { optional: true });

  override readonly bottomTemplate = viewChild('bottomTpl', { read: TemplateRef });

  readonly data = input.required<DocumentFormData>();
  readonly isEmbedded = input(false);
  readonly local = signal<DocumentFormData>(EMPTY_DOCUMENT_FORM_DATA);
  readonly next = output<DocumentFormData>();
  readonly dataChange = output<DocumentFormData>();
  readonly create = output<DocumentFormData>();

  protected readonly address = computed<AddressFormValue>(
    () => this.local().manualAddressValue ?? EMPTY_ADDRESS
  );

  protected readonly complete = computed(() =>
    isCustomerAddressComplete(this.address())
  );

  protected readonly customerLabel = computed(() => {
    const customer = this.local().customer;
    if (!customer) return '';
    return [customer.customerNumber, customer.name1].filter(Boolean).join(' · ');
  });

  ngOnInit(): void {
    const data = untracked(() => this.data());
    // Entering the step is the opt-in: the address is required from here on.
    this.local.set({
      ...data,
      manualCustomerAddressEnabled: true,
      manualAddressValue: data.manualAddressValue ?? { ...EMPTY_ADDRESS },
    });
    this.dataChange.emit(this.local());
  }

  protected setField(field: keyof AddressFormValue, value: string | null): void {
    const address = { ...this.address(), [field]: value };
    this.local.update((d) => ({ ...d, manualAddressValue: address }));
    this.dataChange.emit(this.local());
  }

  protected confirm(): void {
    if (!this.complete()) return;
    const result = this.local();
    if (this.modalResolver && !this.isEmbedded()) {
      this.resolve(result);
    } else {
      this.next.emit(result);
    }
  }
}
